import { CSSProperties } from 'react'
import { z } from 'zod'
import { themeSchema } from '../schema/document'
import { resolveFonts, stackFor } from './fonts'
import { DerivedPalette, derivePalette, paletteToCssVariables } from './palette'

export type Theme = z.infer<typeof themeSchema>

/**
 * The theme as class names.
 *
 * Every palette, radius and button style is a class in `site.scss` that sets
 * custom properties. No colour crosses from TypeScript into the page: the
 * document stores an id, the stylesheet owns the values, and there is nowhere
 * for a hand-typed hex to enter (§2 of the brief).
 *
 * The typefaces are the exception, and they are handled in `themeStyle` rather
 * than here: they name real font families that a `<link>` has to request as
 * well, so the two would drift the moment one of them was edited alone.
 */
export function themeClassName(theme: Theme): string {
  return [
    'mp-page',
    `mp-theme--${theme.paletteId}`,
    `mp-radius--${theme.radius}`,
    `mp-buttons--${theme.buttonStyle}`
  ].join(' ')
}

/**
 * The theme as inline custom properties.
 *
 * The two typefaces always: they are the families `renderPage` asks Google for,
 * so the stack the page is set in and the stylesheet it requests cannot drift
 * apart. Colours only when the venue set a brand colour or overrode a role,
 * because the preset palettes are classes in the stylesheet and an inline
 * property beats a class for the roles that were overridden.
 *
 * Computed geometry's cousin: these are computed VALUES, so an inline style is
 * the only place they can live.
 */
export function themeStyle(theme: Theme): CSSProperties {
  /*
    The faces, always. `resolveFonts` is the only place that decides which
    family a theme is set in, and `renderPage` asks Google for the families it
    returns, so the stack the page is set in and the stylesheet it requests
    cannot disagree.
  */
  const families = resolveFonts(theme)
  const fonts = {
    '--mp-font-heading': stackFor(families.heading),
    '--mp-font-body': stackFor(families.body)
  }

  const derived = theme.brandColor ? derivePalette(theme.brandColor) : undefined
  const overrides = theme.colors ?? {}

  // No colours to say: the preset classes in the stylesheet carry those.
  if (!derived && Object.keys(overrides).length === 0) {
    return fonts as CSSProperties
  }

  /*
    Overrides on top of the derivation, and an override alone is fine too: an
    inline custom property beats the same property from a class, so a single
    colour set in the builder wins over the preset for that role only.
  */
  const palette = {
    ...(derived ?? {}),
    ...overrides
  } as Partial<DerivedPalette>
  const variables = paletteToCssVariables(palette as DerivedPalette)
  const defined = Object.fromEntries(
    Object.entries(variables).filter(([, value]) => value !== undefined)
  )
  return { ...fonts, ...defined } as CSSProperties
}
